import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSignup } from "../hooks/useSignup";
import logo from "../assets/images/login-logo.png";
import "./AuthStyle.css";

const SignupComponent = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const { signup, error: signupError, isLoading } = useSignup();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const { firstName, lastName, email, password } = formData;

    if (!firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (firstName.trim().length < 3) {
      newErrors.firstName = "First name must be at least 3 characters";
    }

    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (lastName.trim().length < 3) {
      newErrors.lastName = "Last name must be at least 3 characters";
    }

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      newErrors.password = "Must include at least one special character";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await signup(
        formData.firstName,
        formData.lastName,
        formData.email,
        formData.password
      );
    } catch (error) {
      console.error("Signup error:", error.message);
      setErrors({ message: error.message });
    }
  };

  return (
    <div className="container vh-100 bg-light shadow shadow-lg">
      <div className="row h-100 g-0">
        {/* Left Panel - Hero Section */}
        <div className="col-lg-6 d-none d-lg-flex bg-primary text-white p-5">
          <div className="d-flex flex-column justify-content-center">
            <h1 className="display-4 fw-bold mb-4">Join Our Community</h1>
            <p className="fs-5 mb-5">
              Create your account and unlock access to all features and
              resources
            </p>

            <div className="d-flex flex-column gap-3">
              <div className="d-flex align-items-center gap-3">
                <i className="fa fa-check-circle fs-4"></i>
                <span className="fs-5">Access to premium courses</span>
              </div>
              <div className="d-flex align-items-center gap-3">
                <i className="fa fa-check-circle fs-4"></i>
                <span className="fs-5">Personalized dashboard</span>
              </div>
              <div className="d-flex align-items-center gap-3">
                <i className="fa fa-check-circle fs-4"></i>
                <span className="fs-5">Job application tracking</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Signup Form */}
        <div className="col-lg-6 d-flex align-items-center justify-content-center p-4">
          <div className="w-100" style={{ maxWidth: "500px" }}>
            <div className="text-center mb-5">
              <img
                src={logo}
                alt="Logo"
                className="img-fluid mb-4"
                style={{ height: "70px" }}
              />
              <h2 className="fw-bold mb-3">Create Account</h2>
              <p className="text-muted">Fill in your details to get started</p>
            </div>

            {signupError && (
              <div className="alert alert-danger d-flex align-items-center mb-4">
                <i className="fa fa-exclamation-circle me-2"></i>
                <div>{signupError}</div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mb-3">
              <div className="row">
                {/* First Name */}
                <div className="col-md-6 mb-4">
                  <div className="input-group border-bottom pb-2">
                    <span className="input-group-text bg-transparent border-0 pe-1">
                      <i className="fa fa-user text-muted"></i>
                    </span>
                    <div className="form-floating flex-grow-1">
                      <input
                        type="text"
                        className={`form-control border-0 ps-0 shadow-none ${
                          errors.firstName ? "is-invalid" : ""
                        }`}
                        id="firstName"
                        name="firstName"
                        placeholder=" "
                        value={formData.firstName}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="firstName" className="text-muted">
                        First Name
                      </label>
                    </div>
                  </div>
                  {errors.firstName && (
                    <div className="invalid-feedback d-flex align-items-center mt-1">
                      <i className="fa fa-exclamation-circle me-1"></i>
                      {errors.firstName}
                    </div>
                  )}
                </div>

                {/* Last Name */}
                <div className="col-md-6 mb-4">
                  <div className="input-group border-bottom pb-2">
                    <span className="input-group-text bg-transparent border-0 pe-1">
                      <i className="fa fa-user text-muted"></i>
                    </span>
                    <div className="form-floating flex-grow-1">
                      <input
                        type="text"
                        className={`form-control border-0 ps-0 shadow-none ${
                          errors.lastName ? "is-invalid" : ""
                        }`}
                        id="lastName"
                        name="lastName"
                        placeholder=" "
                        value={formData.lastName}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="lastName" className="text-muted">
                        Last Name
                      </label>
                    </div>
                  </div>
                  {errors.lastName && (
                    <div className="invalid-feedback d-flex align-items-center mt-1">
                      <i className="fa fa-exclamation-circle me-1"></i>
                      {errors.lastName}
                    </div>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="mb-4">
                <div className="input-group border-bottom pb-2">
                  <span className="input-group-text bg-transparent border-0 pe-1">
                    <i className="fa fa-envelope text-muted"></i>
                  </span>
                  <div className="form-floating flex-grow-1">
                    <input
                      type="email"
                      className={`form-control border-0 ps-0 shadow-none ${
                        errors.email ? "is-invalid" : ""
                      }`}
                      id="email"
                      name="email"
                      placeholder=" "
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="email" className="text-muted">
                      Email Address
                    </label>
                  </div>
                </div>
                {errors.email && (
                  <div className="invalid-feedback d-flex align-items-center mt-1">
                    <i className="fa fa-exclamation-circle me-1"></i>
                    {errors.email}
                  </div>
                )}
              </div>

              {/* Password */}
              <div className="mb-4">
                <div className="input-group border-bottom pb-2">
                  <span className="input-group-text bg-transparent border-0 pe-1">
                    <i className="fa fa-lock text-muted"></i>
                  </span>
                  <div className="form-floating flex-grow-1 position-relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`form-control border-0 ps-0 shadow-none ${
                        errors.password ? "is-invalid" : ""
                      }`}
                      id="password"
                      name="password"
                      placeholder=" "
                      value={formData.password}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="password" className="text-muted">
                      Password
                    </label>
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2"
                      onClick={togglePasswordVisibility}
                      style={{ zIndex: 5 }}
                    >
                      <i
                        className={`fa ${
                          showPassword ? "fa-eye-slash" : "fa-eye"
                        } text-secondary`}
                      ></i>
                    </button>
                  </div>
                </div>
                {errors.password && (
                  <div className="invalid-feedback d-flex align-items-center mt-1">
                    <i className="fa fa-exclamation-circle me-1"></i>
                    {errors.password}
                  </div>
                )}
                <small className="form-text text-muted mt-2">
                  Must be at least 8 characters with one special character
                </small>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="btn btn-primary btn-lg w-100 mb-4 py-3 fw-bold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2"></i> Creating
                    account...
                  </>
                ) : (
                  <>
                    <i className="fa fa-user-plus me-2"></i> Register Now
                  </>
                )}
              </button>

              {/* Login Link */}
              <div className="text-center text-muted small mt-4">
                Already have an account?{" "}
                <Link
                  to="/api/users/login"
                  className="text-primary fw-medium text-decoration-none"
                >
                  Sign In
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupComponent;
